import { useCallback, useEffect, useRef, useState } from 'react'

import TranscriptionsApiService from '@/apis/transcriptionsApiService'

interface UseVoiceRecordingOptions {
  onTranscript: (text: string) => void
}

interface UseVoiceRecordingReturn {
  isRecording: boolean
  isTranscribing: boolean
  duration: number
  error: string | null
  startRecording: () => void
  stopRecording: () => void
}

export default function useVoiceRecording({
  onTranscript,
}: UseVoiceRecordingOptions): UseVoiceRecordingReturn {
  const [isRecording, setIsRecording] = useState(false)
  const [isTranscribing, setIsTranscribing] = useState(false)
  const [duration, setDuration] = useState(0)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const cleanup = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop())
      streamRef.current = null
    }
    mediaRecorderRef.current = null
    chunksRef.current = []
  }, [])

  const startRecording = useCallback(async () => {
    setError(null)

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      let mimeType = ''
      if (MediaRecorder.isTypeSupported('audio/webm')) {
        mimeType = 'audio/webm'
      } else if (MediaRecorder.isTypeSupported('audio/mp4')) {
        mimeType = 'audio/mp4'
      }

      const recorder = mimeType
        ? new MediaRecorder(stream, { mimeType })
        : new MediaRecorder(stream)

      mediaRecorderRef.current = recorder
      chunksRef.current = []

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data)
      }

      recorder.start()
      setIsRecording(true)
      setDuration(0)

      timerRef.current = setInterval(() => {
        setDuration((d) => d + 1)
      }, 1000)
    } catch (err) {
      const mediaError = err as DOMException
      if (mediaError.name === 'NotAllowedError') {
        setError('Microphone access denied')
      } else if (mediaError.name === 'NotFoundError') {
        setError('No microphone found')
      } else {
        setError('Could not start recording')
      }
      cleanup()
    }
  }, [cleanup])

  const stopRecording = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (!recorder || recorder.state === 'inactive') return

    recorder.onstop = async () => {
      const blob = new Blob(chunksRef.current, {
        type: recorder.mimeType || 'audio/webm',
      })
      cleanup()
      setIsRecording(false)

      setIsTranscribing(true)
      try {
        const text = await TranscriptionsApiService.create(blob)
        onTranscript(text)
      } catch {
        setError('Transcription failed')
      }
      setIsTranscribing(false)
    }

    recorder.stop()
  }, [cleanup, onTranscript])

  useEffect(
    () => () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop()
      }
      cleanup()
    },
    [cleanup],
  )

  return { isRecording, isTranscribing, duration, error, startRecording, stopRecording }
}
